/**
 * Connection card — Connect / Disconnect / Test
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
  useZohoStatus, useZohoConnect, useZohoDisconnect, useZohoTest,
} from '@/hooks/useZoho';
import { Link2, Link2Off, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export function ZohoConnectionCard() {
  const { t } = useTranslation();
  const status = useZohoStatus();
  const connect = useZohoConnect();
  const disconnect = useZohoDisconnect();
  const test = useZohoTest();
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const handleConnect = async () => {
    try {
      const { auth_url } = await connect.mutateAsync();
      // Open Zoho consent screen in new tab
      window.open(auth_url, '_blank', 'width=600,height=700');
    } catch {
      // error displayed in mutation state
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('zoho.disconnect_confirm', 'Disconnect from Zoho Books?'))) return;
    await disconnect.mutateAsync();
    setTestResult(null);
    setTestError(null);
  };

  const handleTest = async () => {
    setTestResult(null);
    setTestError(null);
    try {
      const r = await test.mutateAsync();
      if (r.ok && r.organization) {
        setTestResult(`${r.organization.name} (${r.organization.currency_code} · ${r.organization.country})`);
      } else {
        setTestResult(t('zoho.test_no_org', 'Connected, but organization not found'));
      }
    } catch (e) {
      setTestError(e instanceof Error ? e.message : t('zoho.test_failed', 'Test failed'));
    }
  };

  if (status.isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const s = status.data;

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {s?.connected ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span>{t('zoho.connected', 'Connected to Zoho Books')}</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <span>{t('zoho.not_connected', 'Not connected')}</span>
              </>
            )}
          </h3>
          {s?.connected && (
            <div className="mt-2 text-sm text-muted-foreground space-y-0.5">
              {s.org_name && <div><span className="font-medium">{t('zoho.org', 'Organization')}:</span> {s.org_name}</div>}
              {s.org_id && <div><span className="font-medium">{t('zoho.org_id', 'Org ID')}:</span> {s.org_id}</div>}
              {s.data_center && <div><span className="font-medium">{t('zoho.data_center', 'Data center')}:</span> .{s.data_center}</div>}
              {s.connected_at && (
                <div>
                  <span className="font-medium">{t('zoho.connected_at', 'Connected')}:</span>{' '}
                  {new Date(s.connected_at).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          {!s?.connected ? (
            <Button onClick={handleConnect} disabled={connect.isPending} variant="gold">
              {connect.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              {t('zoho.connect_button', 'Connect to Zoho')}
            </Button>
          ) : (
            <>
              <Button onClick={handleTest} disabled={test.isPending} variant="outline" size="sm">
                {test.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {t('zoho.test_button', 'Test connection')}
              </Button>
              <Button onClick={handleDisconnect} disabled={disconnect.isPending} variant="ghost" size="sm">
                <Link2Off className="h-4 w-4 mr-2" />
                {t('zoho.disconnect_button', 'Disconnect')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Test result */}
      {testResult && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          ✓ {testResult}
        </div>
      )}
      {testError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          ✗ {testError}
        </div>
      )}

      {/* Connect-flow guidance */}
      {!s?.connected && !connect.isPending && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <p className="font-medium mb-1">{t('zoho.howto_title', 'How it works:')}</p>
          <ol className="list-decimal list-inside space-y-0.5 text-xs">
            <li>{t('zoho.howto_1', 'Click "Connect to Zoho" — a new tab opens with Zoho consent.')}</li>
            <li>{t('zoho.howto_2', 'Approve the access request on Zoho.')}</li>
            <li>{t('zoho.howto_3', 'You\'ll be redirected back; the tab auto-closes.')}</li>
            <li>{t('zoho.howto_4', 'Refresh this page to see "Connected".')}</li>
          </ol>
        </div>
      )}
    </div>
  );
}
